import { engineComponent, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { IPickRound } from "neo-games/Engine/Communication/Game/Types/IPickRound";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { IPickRoundSpot } from "neo-games/Engine/Communication/Game/Types/IPickRoundSpot";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { Languages } from "../Utils/Languages";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { WinAnimatorFs } from "./WinAnimatorFs";
import { GameManager } from "../Managers/GameManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { PlayList } from "../Components/Sounds/PlayList";

/**
 * WinAnimatorPick uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimatorPick
 * @extends ContainerElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorPick extends WinAnimatorFs {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly ShowWinValueFinished: string = "eventShowWinValueFinished";

    public static readonly ShowPickMultiplierFinished: string = "eventShowPickMultiplierFinished";

    public static readonly ShowFinalTableFinished: string = "eventShowFinalTableFinished";

    //******************************************************************************************************************
    //  TIMER STATUSES
    //******************************************************************************************************************

    private static readonly ValueTeaseAnimationTimer: string = "valueTeaseAnimationTimer";

    private static readonly ValueAnimationTimer: string = "valueAnimationTimer";
    private static readonly ValueAnimationTimerStep2: string = "valueAnimationTimerStep2";
    private static readonly ValueAnimationTimerStep3: string = "valueAnimationTimerStep3";
    private static readonly ValueAnimationTimerStep4: string = "valueAnimationTimerStep4";

    private static readonly PickMultiplierTeaseAnimationTimer: string = "pickMultiplierTeaseAnimationTimer";

    private static readonly PickMultiplierAnimationTimer: string = "pickMultiplierAnimationTimer";
    private static readonly PickMultiplierAnimationTimerStep2: string = "pickMultiplierAnimationTimerStep2";

    private static readonly PickMultiplierCountAnimationTimer: string = "pickMultiplierCountAnimationTimer";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static readonly ValueTeaseAnimationDuration: number = 90; //in frames

    private static readonly ValueAnimationDuration: number = 90; //in frames
    private static readonly ValueAnimationDurationStep2: number = 60; //in frames
    private static readonly ValueAnimationDurationStep3: number = 30; //in frames
    private static readonly ValueAnimationDurationStep4: number = 60; //in frames

    private static readonly PickMultiplierTeaseAnimationDuration: number = 120; //in frames;

    private static readonly PickMultiplierAnimationDuration: number = 70; //in frames; WIN_MULTIPLIER_2 + WIN_MULTIPLIER_3 + WIN_MULTIPLIER_4 + delay to final table
    private static readonly PickMultiplierAnimationDurationStep2: number = 60; //in frames; WIN_MULTIPLIER_2 + WIN_MULTIPLIER_3 + WIN_MULTIPLIER_4 + delay to final table

    private static readonly PickMultiplierCountAnimationDuration: number = 20; //in frames;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected spots: Array<SwitchElement>;

    protected currentRound: IPickRound;

    protected pickValueData: IPickRoundSpot;

    protected pickMultiplierData: IPickRoundSpot;

    protected currentMultiplierCount: number;

    //******************************************************************************************************************
    //  INIT
    //******************************************************************************************************************

    initializePick(spots: Array<SwitchElement>): void {
        this.spots = spots;
        this.createTimer();
    }

    //******************************************************************************************************************
    //  TABLES
    //******************************************************************************************************************

    public showScore(init: boolean = false): void {
        if (this.score != null) {
            if (init) {
                if (this.currentRound != null) {
                    switch (this.currentRound.remainingPicks) {
                        case 2:
                            this.score.goToCase("SHOW_INIT");
                            break;
                        case 1:
                            this.score.goToCase("WIN_VALUE_INIT");
                            break;
                        case 0:
                            this.score.goToCase("EMPTY");
                            break;
                    }
                }
            } else {
                this.score.goToCase("SHOW");
            }
        }
    }

    private showScoreValue(spotPosition: PIXI.Point, data: IPickRoundSpot, init: boolean = false): void {
        if (this.score != null) {
            this.pickValueData = data;
            let flare = this.score.getChildElementByName("Flare") as SwitchElement;
            let counter = this.score.getChildElementByName("Value") as ImageTextCounterElement;
            let scoreCounter = this.getScoreCounter("PickTableWin");

            if (counter != null && scoreCounter != null) {
                scoreCounter.setBitmapText(data.value);

                if (!init) {
                    flare.x = spotPosition.x;
                    flare.y = spotPosition.y;
                    counter.position.x = spotPosition.x;
                    counter.position.y = spotPosition.y;

                    this.score.goToCase("WIN_VALUE");
                    counter.start(0, data.value);
                } else {
                    counter.setBitmapText(data.value);
                }
            }
        }
    }

    private showScoreMultiplier(spotPosition: PIXI.Point, data: IPickRoundSpot, init: boolean = false): void {
        if (this.score != null) {
            this.pickMultiplierData = data;
            let flare = this.score.getChildElementByName("Flare") as SwitchElement;
            let multiplier = this.score.getChildElementByName("Multiplier") as ImageTextCounterElement;
            let scoreMultiplier = this.getScoreCounter("PickTableMtp");

            if (multiplier != null) {
                scoreMultiplier.setBitmapText(data.value);

                if (!init) {
                    flare.x = spotPosition.x;
                    flare.y = spotPosition.y;
                    multiplier.position.x = spotPosition.x;
                    multiplier.position.y = spotPosition.y;

                    multiplier.setBitmapText(1);

                    this.score.goToCase("WIN_MULTIPLIER");

                    this.currentMultiplierCount = 0;

                    this.stopTimer();
                    this.startTimer(WinAnimatorPick.PickMultiplierCountAnimationTimer, 5); //WIN_MULTIPLIER animation duration
                }
            }
        }
    }

    private countMultiplier(): void {
        if (this.currentMultiplierCount < this.pickMultiplierData.value) {
            this.currentMultiplierCount++;

            const flare = this.score.getChildElementByName("Flare") as SwitchElement;
            const multiplier = this.score.getChildElementByName("Multiplier") as ImageTextCounterElement;
            const spotElement = this.spots[this.pickMultiplierData.id];

            if (multiplier != null && spotElement != null) {
                multiplier.setBitmapText(this.currentMultiplierCount);

                const spotPosition = (<ImageTextCounterElement>(
                    spotElement.getChildElementByName("Value")
                )).getGlobalPosition();
                flare.x = spotPosition.x;
                flare.y = spotPosition.y;
                multiplier.position.x = spotPosition.x;
                multiplier.position.y = spotPosition.y;

                spotElement.goToCase("POP");
                PlayList.play("SND_Bonus_credit_Butterfly");

                this.score.goToCase("WIN_MULTIPLIER_POP");
            }

            this.stopTimer();
            this.startTimer(
                WinAnimatorPick.PickMultiplierCountAnimationTimer,
                WinAnimatorPick.PickMultiplierCountAnimationDuration,
            );
        } else {
            this.score.goToCase("WIN_MULTIPLIER_2");
            PlayList.play("SND_Bonus_creditFinal_Butterfly");

            this.stopTimer();
            this.startTimer(
                WinAnimatorPick.PickMultiplierAnimationTimer,
                WinAnimatorPick.PickMultiplierAnimationDuration,
            );
        }
    }

    protected showFinalTableAnimation(init: boolean = false): void {
        if (this.currentRound != null && this.currentRound.remainingPicks == 0) {
            if (this.score != null && this.finalTable != null) {
                this.score.goToCase("SHOW_FINAL_TABLE");
                this.finalTable.goToCase("SHOW_FINAL_TABLE");
                PlayList.stop("SND_BonusLoop");
                PlayList.play("SND_FSEnd");

                const scoreValue = this.getScoreCounter("PickTableWin");
                const tableValue = this.finalTable.getChildElementByName("WinCounter") as ImageTextCounterElement;

                if (scoreValue && tableValue) {
                    if (init) {
                        this.score.goToCase("EMPTY");
                        this.finalTable.goToCase("SHOW_FINAL_TABLE_INIT");

                        tableValue.setBitmapText(GameManager.pickResponse.totalWin);
                    } else {
                        this.finalCount = 0;
                        PlayList.loop("SND_BaseCreditboxLoop2");
                        scoreValue.start(
                            this.pickValueData.value,
                            0,
                            WinAnimatorFs.FinalTableCountingAnimationDuration,
                            0,
                        );
                        tableValue.start(
                            0,
                            this.pickValueData.value,
                            WinAnimatorFs.FinalTableCountingAnimationDuration,
                            0,
                        );

                        this.startTimer(
                            WinAnimatorFs.FinalTableMultiplierAnimationTimer,
                            WinAnimatorFs.FinalTableCountingAnimationDuration,
                        );
                    }
                }
            }
        }
    }

    protected updateFinalValue(): void {
        if (this.finalCount > 0 && this.finalCount < this.pickMultiplierData.value) {
            PlayList.stop("SND_BaseCreditboxLoop2");
            const tableValue = this.finalTable.getChildElementByName("WinCounter") as ImageTextCounterElement;
            const scoreMultiplier = this.getScoreCounter("PickTableMtp");
            const currentTotalWin = parseFloat(NumberUtils.multiply(this.pickValueData.value, this.finalCount + 1));

            this.score.goToCase("POP");

            scoreMultiplier.setBitmapText(this.pickMultiplierData.value - this.finalCount);
            tableValue.setBitmapText(currentTotalWin);
        }

        this.finalTable.goToCase("POP");

        if (this.finalCount < this.pickMultiplierData.value) {
            PlayList.play("SND_BaseCreditboxEnd");
            this.finalCount++;
            this.startTimer(
                WinAnimatorFs.FinalTableMultiplierAnimationTimer,
                WinAnimatorFs.FinalTableMultiplierAnimationDuration,
            );
        } else {
            PlayList.play("SND_FSTotalWinEnd");
            this.score.goToCase("HIDE");
            this.finalTable.goToCase("FINISH");
            this.startTimer(WinAnimatorFs.FinalTableAnimationTimer, WinAnimatorFs.FinalTableAnimationDuration);
        }
    }

    //******************************************************************************************************************
    //  SHOW PICK WINS
    //******************************************************************************************************************

    public showPickWins(round: IPickRound): void {
        this.currentRound = round;
        this.currentWin = round.totalWin;

        if (this.currentRound.remainingPicks == 1) {
            this.teasePickValue();
        } else {
            this.teasePickMultiplier();
        }
    }

    public showInitPick(round: IPickRound): void {
        this.currentRound = round;
        this.currentWin = round.totalWin;

        for (let i = 0; i < this.currentRound.spots.length; i++) {
            this.spotGoToCaseByStatus(this.currentRound.spots[i], true);
        }

        if (this.currentRound.remainingPicks < 2) {
            this.hidePickValueSpots(true);
        }
    }

    private teasePickValue(): void {
        if (this.currentRound != null) {
            let valueSpots = this.currentRound.spots.filter(
                (value) => value.valueType == Communication.Game.PickRoundSpotValueType.Cash,
            );

            for (let i = 0; i < valueSpots.length; i++) {
                const spotData = valueSpots[i];

                if (spotData != null) {
                    const spotElement = this.spots[spotData.id];

                    if (spotData.status == Communication.Game.PickRoundSpotStatus.Revealed) {
                        spotElement.goToCase("TEASE");
                    } else {
                        spotElement.goToCase("INACTIVE");
                    }
                }
            }
        }

        this.stopTimer();
        this.startTimer(WinAnimatorPick.ValueTeaseAnimationTimer, WinAnimatorPick.ValueTeaseAnimationDuration);
    }

    private showPickValue(): void {
        if (this.currentRound != null) {
            let valueSpots = this.currentRound.spots.filter(
                (value) => value.valueType == Communication.Game.PickRoundSpotValueType.Cash,
            );

            for (let i = 0; i < valueSpots.length; i++) {
                this.spotGoToCaseByStatus(valueSpots[i]);
            }
        }
        PlayList.play("SND_BaseCreditboxLoop2");
        this.stopTimer();
        this.startTimer(WinAnimatorPick.ValueAnimationTimer, WinAnimatorPick.ValueAnimationDuration);
    }

    private showPickValueStep2(): void {
        PlayList.stop("SND_BaseCreditboxLoop2");
        PlayList.play("SND_BaseCreditboxEnd");
        this.stopTimer();
        this.startTimer(WinAnimatorPick.ValueAnimationTimerStep2, WinAnimatorPick.ValueAnimationDurationStep2);
    }

    private showPickValueStep3(): void {
        PlayList.play("SND_Bonus_creditFinal_Egg");
        this.stopTimer();
        this.startTimer(WinAnimatorPick.ValueAnimationTimerStep3, WinAnimatorPick.ValueAnimationDurationStep3);
    }

    private showPickValueStep4(): void {
        PlayList.play("SND_BonusTrophy");
        this.stopTimer();
        this.startTimer(WinAnimatorPick.ValueAnimationTimerStep4, WinAnimatorPick.ValueAnimationDurationStep4);
    }

    private showPickValueFinished(): void {
        this.emit(WinAnimatorPick.ShowWinValueFinished);
    }

    public hidePickValueSpots(init: boolean = false): void {
        if (this.currentRound != null) {
            for (let i = 0; i < this.spots.length; i++) {
                let spotsData = this.currentRound.spots[i];
                let revealed = spotsData.status == Communication.Game.PickRoundSpotStatus.Revealed;

                if (init) {
                    this.spots[i].goToCase(i < 5 ? "EMPTY" : spotsData.status.toString().toUpperCase() + "_INIT");
                } else {
                    this.spots[i].goToCase(
                        i < 5 ? (revealed ? "HIDE_REVEALED" : "HIDE") : spotsData.status.toString().toUpperCase(),
                    );
                }
            }
        }
    }

    private teasePickMultiplier(): void {
        if (this.currentRound != null) {
            let valueSpots = this.currentRound.spots.filter(
                (value) => value.valueType == Communication.Game.PickRoundSpotValueType.Multiplier,
            );

            for (let i = 0; i < valueSpots.length; i++) {
                const spotData = valueSpots[i];

                if (spotData != null) {
                    const spotElement = this.spots[spotData.id];

                    if (spotData.status == Communication.Game.PickRoundSpotStatus.Revealed) {
                        spotElement.goToCase("TEASE");
                    } else {
                        spotElement.goToCase("INACTIVE");
                    }
                }
            }
        }

        this.stopTimer();
        this.startTimer(
            WinAnimatorPick.PickMultiplierTeaseAnimationTimer,
            WinAnimatorPick.PickMultiplierTeaseAnimationDuration,
        );
    }

    private showPickMultiplier() {
        if (this.currentRound != null) {
            let valueSpots = this.currentRound.spots.filter(
                (value) => value.valueType == Communication.Game.PickRoundSpotValueType.Multiplier,
            );

            for (let i = 0; i < valueSpots.length; i++) {
                this.spotGoToCaseByStatus(valueSpots[i]);
            }
        }
        //
        // this.stopTimer();
        // this.startTimer(WinAnimatorPick.PickMultiplierAnimationTimer, WinAnimatorPick.PickMultiplierAnimationDuration);
    }

    private showPickMultiplierBeforeFinished(): void {
        PlayList.play("SND_Bonus_Green_field");

        this.stopTimer();
        this.startTimer(
            WinAnimatorPick.PickMultiplierAnimationTimerStep2,
            WinAnimatorPick.PickMultiplierAnimationDurationStep2,
        );
    }

    private showPickMultiplierFinished(): void {
        this.emit(WinAnimatorPick.ShowPickMultiplierFinished);
    }

    //******************************************************************************************************************
    //  SPOTS
    //******************************************************************************************************************

    private spotGoToCaseByStatus(spotData: IPickRoundSpot, init: boolean = false): void {
        let spotElement = this.spots[spotData.id];

        if (spotElement != null) {
            this.updateSpotValue(spotElement, spotData);
            spotElement.goToCase(spotData.status.toString().toUpperCase());
            let spotValue = <ImageTextElement>spotElement.getChildElementByName("Value");

            if (spotData.status == Communication.Game.PickRoundSpotStatus.Revealed) {
                if (spotData.valueType == Communication.Game.PickRoundSpotValueType.Cash) {
                    this.showScoreValue(spotValue.getGlobalPosition(), spotData, init);
                } else {
                    this.showScoreMultiplier(spotValue.getGlobalPosition(), spotData, init);
                }
            }
        }
    }

    private updateSpotValue(spotElement: SwitchElement, spotData: IPickRoundSpot): void {
        if (spotElement != null) {
            let spotValueElement = spotElement.getChildElementByName("Value") as ImageTextElement;

            if (spotValueElement != null && spotData != null && spotData.value != null) {
                if (spotData.valueType === Communication.Game.PickRoundSpotValueType.Cash) {
                    spotValueElement.bitmapText.text = Languages.formatText(
                        Languages.FormatMoney,
                        spotData.value.toString(),
                    );
                } else if (spotData.valueType === Communication.Game.PickRoundSpotValueType.Multiplier) {
                    spotValueElement.bitmapText.text = "x" + spotData.value.toString();
                }

                spotValueElement.realign();
            }
        }
    }

    public resetSpots(): void {
        if (this.score != null) {
            this.score.goToCase("EMPTY");
        }

        if (this.finalTable != null) {
            this.finalTable.goToCase("EMPTY");
        }

        for (let i = 0; i < this.spots.length; i++) {
            this.spots[i].goToCase("EMPTY");
        }
    }

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorPick.ValueTeaseAnimationTimer:
                this.showPickValue();
                break;
            case WinAnimatorPick.ValueAnimationTimer:
                this.showPickValueStep2();
                break;
            case WinAnimatorPick.ValueAnimationTimerStep2:
                this.showPickValueStep3();
                break;
            case WinAnimatorPick.ValueAnimationTimerStep3:
                this.showPickValueStep4();
                break;
            case WinAnimatorPick.ValueAnimationTimerStep4:
                this.showPickValueFinished();
                break;
            case WinAnimatorPick.PickMultiplierTeaseAnimationTimer:
                this.showPickMultiplier();
                break;
            case WinAnimatorPick.PickMultiplierCountAnimationTimer:
                this.countMultiplier();
                break;
            case WinAnimatorPick.PickMultiplierAnimationTimer:
                this.showPickMultiplierBeforeFinished();
                break;
            case WinAnimatorPick.PickMultiplierAnimationTimerStep2:
                this.showPickMultiplierFinished();
                break;
            default:
                super.onTimerComplete();
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorPick.ts
 **/